import { Thumbmark } from "@thumbmarkjs/thumbmarkjs";
import { hashValue } from "./utils/hash";
import { safe, safeAsync } from "./utils/safe";

export interface ThumbmarkResult {
  thumbmark: string | null;
  version: string | null;
  canvasHash: string | null;
  audioHash: string | null;
  fontsHash: string | null;
  mathHash: string | null;
  pluginsHash: string | null;
}

/**
 * Run ThumbmarkJS for the base fingerprint (canvas/audio/fonts/screen/math/plugins…).
 * We derive stable per-probe hashes and the `thumbmark` device id from the raw components;
 * the raw components themselves are not kept. WebGL/headless extras are collected separately.
 */
export async function runThumbmark(timeoutMs: number): Promise<ThumbmarkResult> {
  const empty: ThumbmarkResult = {
    thumbmark: null,
    version: null,
    canvasHash: null,
    audioHash: null,
    fontsHash: null,
    mathHash: null,
    pluginsHash: null,
  };

  return safeAsync(async () => {
    const tm = new Thumbmark({ timeout: timeoutMs });
    const result = (await tm.get()) as {
      thumbmark?: string;
      version?: string;
      components?: Record<string, unknown>;
    };

    const components = result.components ?? {};
    return {
      thumbmark: result.thumbmark ?? null,
      version: result.version ?? null,
      canvasHash: hashComponent(components, "canvas"),
      audioHash: hashComponent(components, "audio"),
      fontsHash: hashComponent(components, "fonts"),
      mathHash: hashComponent(components, "math"),
      pluginsHash: hashComponent(components, "plugins"),
    };
  }, empty);
}

/**
 * ThumbmarkJS folds each probe into `components.<name>`; hash the named one to a stable
 * scalar field (canvas/audio/fonts/math/plugins). Absent component → null.
 */
function hashComponent(components: Record<string, unknown>, name: string): string | null {
  return safe(() => {
    const value = components[name];
    if (value == null) return null;
    return hashValue(value);
  }, null);
}
